import { get, find, nth, isEmpty, snakeCase } from 'lodash';
import moment from 'moment';
import { getFromLocalStorage, setInLocalStorage } from 'Client/local-storage-api';
import { momentFromApiDate } from 'Utils/date/apiDate';
import { t } from 'Src/i18n';
import { ACCOMMODATION_COMFORT_ORDER } from 'Utils/accommodation/accommodation.constants';
import { LEGAL_INFO_WEBTHEO_ID, LEGAL_INFO_THEO_ID } from 'Utils/booking-process/constants';
import { pluralizeLabel } from 'Utils/label/labelUtils';
import { formatAmountForLocale } from 'Utils/currency-config-for-locale/currencyConfigForLocale';
import { TRANSPORT_CLASS_TO_I18N } from 'Components/widgets/transport-details/transportUtils';
import {
  PACKAGE_ROOM_ONLY,
  PACKAGE_ALL_INCLUSIVE,
  CEFALU_RESORT,
  DELUXE_COMFORT,
  PRICE_COUNTER_PROPOSAL_THRESHOLD,
  COMFORT_TYPE_SUITE,
  COMFORT_TYPE_CHALET,
  COMFORT_TYPE_VILLA,
} from './constants';
const OPTIONS_DAYS_RANGE = 29;
const DEFAULT_STOCK_WARNING_LIMIT = 2;
const TWO_WEEKS_IN_MS = 14 * 24 * 3600 * 1000;

function getAccommodationComfortIndex(accommodation: Accommodation): number {
  return ACCOMMODATION_COMFORT_ORDER.indexOf(get(accommodation, 'categoryId'));
}

function compareProposalsByComfort(proposal1: Proposal, proposal2: Proposal): number {
  const proposal1Index = getAccommodationComfortIndex(get(proposal1, 'accommodations[0]'));
  const proposal2Index = getAccommodationComfortIndex(get(proposal2, 'accommodations[0]'));

  // if we have not sorted the accommodation we don't change the order returned by the api
  if (proposal1Index === -1 || proposal2Index === -1) {
    return 0;
  }

  return proposal1Index - proposal2Index;
}

function compareAccommodationsByComfort(
  accommodation1: Accommodation,
  accommodation2: Accommodation,
): number {
  return (
    getAccommodationComfortIndex(accommodation1) - getAccommodationComfortIndex(accommodation2)
  );
}

function areAnyTimeSlotIdsPresent(
  attendees: ReadonlyArray<AttendeeWithSchedulesType>,
  targetIds: Array<string>,
): boolean {
  const targetSet = new Set(targetIds);
  return attendees.some((attendee) =>
    attendee.schedules.some((schedule) =>
      schedule.timeSlots.some((slot) => targetSet.has(slot.id)),
    ),
  );
}

function isCharterFlight(cancellationPolicy: string): boolean | null | undefined {
  return [LEGAL_INFO_THEO_ID, LEGAL_INFO_WEBTHEO_ID].includes(cancellationPolicy);
}

function isDateAfterOptionRange(date: moment): boolean {
  return date && date.isValid() && date.diff(moment(), 'days') >= OPTIONS_DAYS_RANGE;
}

function isOptionAllowed(proposal: Proposal): boolean {
  return get(proposal, 'optionAvailable');
}

function getAdultsCount(proposal: Proposal): number {
  return get(proposal, 'attendees.adults.length', 1);
}

function getCurrency(proposal: Proposal): string | null | undefined {
  return get(proposal, 'price.currency');
}

function getKidsCount(proposal: Proposal): number {
  return get(proposal, 'attendees.children.length', 0);
}

function getTotalPriceFormatted(proposal: Proposal): number | null | undefined {
  return get(proposal, 'price.total.formattedValue');
}

function getArrivalDate(proposal: Proposal): moment$Moment {
  return momentFromApiDate(get(proposal, 'arrivalDate'));
}

function getTripDeparture(proposal: Proposal): string {
  return get(proposal, 'transports.inbound.travelSections[0].departure.label', '');
}

function getTripArrival(proposal: Proposal): string {
  return get(proposal, 'transports.outbound.travelSections[0].departure.label', '');
}

function hasChildren(proposal: Proposal): boolean {
  return getKidsCount(proposal) > 0;
}

function hasChildcare(proposal: Proposal): boolean {
  return proposal.additionalServices.childcares.length > 0;
}

function hasAllInclusive(proposal: Proposal): boolean {
  return get(proposal, 'packageId') === PACKAGE_ALL_INCLUSIVE;
}

function getFormattedDate(date: string, dateFormat: string = 'DD MMMM YYYY'): string {
  return momentFromApiDate(date).format(dateFormat);
}

function getFormattedArrivalDate(proposal: Proposal, dateFormat?: string): string {
  return getFormattedDate(get(proposal, 'arrivalDate'), dateFormat);
}

function getFormattedDepartureDate(proposal: Proposal, dateFormat?: string): string {
  return getFormattedDate(get(proposal, 'departureDate'), dateFormat);
}

function getRONPackage(proposal: Proposal): UpsellPackage | null | undefined {
  const packages = get(proposal, 'packages') || [];
  return find(packages, (pkg) => pkg.id === PACKAGE_ROOM_ONLY);
}

function getPackage(proposal: Proposal, packageId: string): UpsellPackage | null | undefined {
  const packages = get(proposal, 'packages') || [];
  return find(packages, (pkg) => pkg?.id === packageId) || {};
}

function hasTransport(proposal: Proposal): boolean {
  return !!get(proposal, 'transports');
}

function hasTransfer(proposal: Proposal): boolean {
  return hasTransport(proposal) || !isEmpty(get(proposal, 'additionalServices.transfers'));
}

function hasTransportOrTransferSelected(proposal: Proposal): boolean {
  return (
    hasTransport(proposal) ||
    !isEmpty(
      (proposal.additionalServices.transfers || []).filter(
        (transfer) => transfer.pricingInfo.isFullyRented,
      ),
    )
  );
}

function isTransportATrain(proposal: Proposal): boolean {
  return (
    get(proposal, 'transports.inbound.travelSections[0].transport.type') === 'TRAIN' &&
    get(proposal, 'transports.outbound.travelSections[0].transport.type') === 'TRAIN'
  );
}

function hasAccomodation(proposal: Proposal): boolean {
  return !!get(proposal, 'accommodations.length');
}

function getResolvedProposals(proposals: Array<Proposal>): Array<Proposal> {
  return proposals.filter((p) => !p.errorCode);
}

function hasErroredProposals(proposals: Array<Proposal>): boolean {
  return proposals.some((p) => p.errorCode);
}

function hasFetchProposalsFailed({
  errors,
  proposals,
}: {
  errors: Array<any>;
  proposals: Array<Proposal>;
  dateSuggestion: Array<any>;
  suggestions: Array<any>;
}): boolean {
  return (
    (errors && errors.length > 0) ||
    hasErroredProposals(proposals) ||
    !proposals ||
    proposals.length === 0 ||
    proposals[0]?.price?.gap >= PRICE_COUNTER_PROPOSAL_THRESHOLD
  );
}

function getSelectedProposal(proposals: Array<Proposal>, selectedProposalIndex: number): Proposal {
  return nth(proposals, selectedProposalIndex);
}

function getSelectedProposalById(
  proposals: Array<Proposal>,
  selectedProposalId: string | null | undefined,
): Proposal | null | undefined {
  return proposals.find((proposal) => proposal.id === selectedProposalId) || proposals[0];
}

function getStockWarning(
  i18n: {
    stockWarningLimit?: number;
    room?: string;
    rooms?: string;
  },
  remainingStock: number,
): {
  showStockWarning: boolean;
  stockWarning: string;
} {
  return {
    showStockWarning: remainingStock <= get(i18n, 'stockWarningLimit', DEFAULT_STOCK_WARNING_LIMIT),
    stockWarning: `${remainingStock} ${pluralizeLabel(get(i18n, 'room', ''), get(i18n, 'rooms', ''), remainingStock)}`,
  };
}

function getNbParticipants(proposal: Proposal): number {
  return get(proposal, 'attendees.list.length', 0);
}

function getTotalValue(proposal?: Proposal): number {
  return get(proposal, 'price.total.value', 0);
}

function getFormatedPrice(formatedPrice: string, amount: number): string {
  if (amount === 0) {
    return `${formatedPrice}`;
  }

  return amount > 0 ? `+${formatedPrice}` : `-${formatedPrice}`;
}

function getFormatedPriceByLocale(price: number, locale: string, currency: string) {
  return getFormatedPrice(formatAmountForLocale(Math.abs(price), locale, currency), price);
}

function getPriceDiff(
  proposalPrice: number,
  selectedProposalPrice: number,
  locale: string,
  currency: string,
): string | null | undefined {
  const diffPrice = proposalPrice - selectedProposalPrice;
  return diffPrice === 0 ? null : getFormatedPriceByLocale(diffPrice, locale, currency);
}

const getDefaultProposal = (
  proposals: ReadonlyArray<Proposal>,
  resort: Product | null | undefined,
) => {
  /**
   * There is a random corner case that make selectedProposal as undefined
   * It should be the default one (mainly the first one).
   * Maybe proposals array is empty at some point...
   * Unable to reproduce the bug... See BookingEngine.container
   */
  const firstProposal = proposals[0] || {};

  if (resort && resort.id === CEFALU_RESORT) {
    const deluxeProposal = proposals.find(
      (proposal) => get(proposal, 'accommodations[0].comfortType') === DELUXE_COMFORT,
    );
    return deluxeProposal ? deluxeProposal : firstProposal;
  }

  return firstProposal;
};

const isPremiumAccommodation = (accommodation: Accommodation) => {
  return !!(
    accommodation.comfortId &&
    [COMFORT_TYPE_SUITE, COMFORT_TYPE_CHALET, COMFORT_TYPE_VILLA].includes(accommodation.comfortId)
  );
};

const isExclusiveCollectionRoom = (
  accommodation: Accommodation,
  product: Product | null | undefined,
) => {
  return accommodation.exclusiveCollection || Number(product?.comfort) >= 5;
};

const isExclusiveCollectionSpace = (
  accommodation: Accommodation,
  product: Product | null | undefined,
) => {
  return accommodation.exclusiveCollection && product?.comfort === 4;
};

function buildTransportDetailKey(transportDetail: TransportDetail): string {
  return transportDetail.travelSections
    .map((section) => {
      const transportClass = TRANSPORT_CLASS_TO_I18N[section.transport?.class];
      const company = `${transportClass}${section.transport?.companyOperator?.id || ''}`;
      return `${section.departure?.id}${section.departure?.date}${section.departure?.time}-${section.arrival?.id}${section.arrival?.date}${section.arrival?.time}-${company}`;
    })
    .join(':');
}

const buildTransportKey = (transport: Transport) => {
  return `${buildTransportDetailKey(transport.inbound)}/${buildTransportDetailKey(transport.outbound)}`;
};

const isRecommendedTransport = (
  transport: Transport | null | undefined,
  proposalId: string,
): boolean => {
  if (!transport || !transport.inbound || !transport.outbound) {
    return false;
  }

  return buildTransportKey(transport) === getFromLocalStorage(`recommendedTransport-${proposalId}`);
};

const saveRecommendedTransport = (transport: Transport, proposalId: string) => {
  const localStorageKey = `recommendedTransport-${proposalId}`;

  if (!getFromLocalStorage(localStorageKey)) {
    setInLocalStorage(localStorageKey, buildTransportKey(transport), {
      ttl: TWO_WEEKS_IN_MS,
    });
  }
};

const getMainHouseholdAttendee = (
  proposal: Proposal,
  householdId: number,
): ProposalAttendeesType | null | undefined => {
  const { customerId } = proposal.households[householdId].attendees[0];
  return proposal.attendees.list.find((attendee) => attendee.customerId === customerId);
};

const ERROR_MESSAGES = {
  RESORT_NOT_AVAILABLE: 'productsComparison.resortNotAvailable',
  NOT_ON_SALE_FOR_THE_PERIOD: 'booking.accommodation.errorMessages.calendar.resortNotOnSale',
  INVALID_PRODUCT_ID: 'booking.accommodation.errorMessages.calendar.resortNotOnSale',
  NO_TRANSPORT: 'booking.accommodation.errorMessages.noTransport',
  NO_ACCOMMODATION: 'booking.accommodation.errorMessages.noAccommodation',
  UNAVAILABLE_FOR_BIRTHDATES: 'booking.accommodation.errorMessages.ageIncorrectOrUnauthorized',
  INVALID_BOOKING_CRITERIA: 'booking.accommodation.errorMessages.invalidBookingCriteria',
  NO_PROPOSAL: 'booking.accommodation.errorMessages.noProposalAvailable',
  TECHNICAL_ERROR: 'booking.accommodation.errorMessages.technicalError',
  INVALID_VOUCHER_CODE: 'booking.accommodation.errorMessages.technicalError',
  AUTHORIZATION_ERROR: 'productsComparison.unknownAttendee',
};
const QUICK_SEARCH_ERROR_MESSAGES = {
  INVALID_PRODUCT_ID: 'booking.partners.errorMessages.invalidResort',
  NO_ACCOMMODATION: 'booking.partners.errorMessages.noAccommodation',
  NOT_ON_SALE_FOR_THE_PERIOD: 'booking.partners.errorMessages.resortNotOnSale',
  NO_TRANSPORT: 'booking.partners.errorMessages.noTransport',
  TOO_MANY_ATTENDEES: 'booking.partners.errorMessages.attendees',
  UNAUTHORIZED_SALES_NETWORK: 'booking.partners.errorMessages.salesNetwork',
  UNAVAILABLE_FOR_BIRTHDATES: 'booking.partners.errorMessages.ageIncorrectOrUnauthorized',
  NO_PROPOSAL: 'booking.partners.errorMessages.noProposalAvailable',
  INVALID_BOOKING_CRITERIA: 'booking.partners.errorMessages.invalidBookingCriteria',
  FIELD_EMPTY: 'booking.partners.errorMessages.emptyField',
  INVALID_RESORT_ARRIVAL_DATE: 'booking.partners.errorMessages.invalidArrivalDate',
  UNKNOWN_DEPARTURE_CITY: 'booking.partners.errorMessages.invalidDepartureCity',
  TECHNICAL_ERROR: 'booking.partners.errorMessages.technicalError',
};
const QUICK_SEARCH_ERROR_LABELS = Object.keys(QUICK_SEARCH_ERROR_MESSAGES);

const toUpperSnakeCase = (errorCode) => snakeCase(errorCode).toUpperCase();

const getQuickSearchErrorMessage = (errorCode: string) => {
  const errorLabel = toUpperSnakeCase(errorCode);
  return QUICK_SEARCH_ERROR_LABELS.includes(errorLabel)
    ? t(QUICK_SEARCH_ERROR_MESSAGES[errorLabel])
    : t('booking.partners.errorMessages.defaultErrorMessage');
};

const isExpiredDepartureDate = (router: any): boolean => {
  return moment(router.query.departureDate).isBefore(moment(new Date()), 'day');
};

export {
  ERROR_MESSAGES,
  buildTransportKey,
  areAnyTimeSlotIdsPresent,
  getAccommodationComfortIndex,
  getAdultsCount,
  getPackage,
  getCurrency,
  getRONPackage,
  getKidsCount,
  getFormattedArrivalDate,
  getFormattedDepartureDate,
  hasAllInclusive,
  hasChildren,
  hasChildcare,
  hasTransfer,
  getTotalPriceFormatted,
  getMainHouseholdAttendee,
  hasTransport,
  isTransportATrain,
  isCharterFlight,
  isDateAfterOptionRange,
  isOptionAllowed,
  isRecommendedTransport,
  saveRecommendedTransport,
  getArrivalDate,
  getTripArrival,
  getTripDeparture,
  hasAccomodation,
  compareAccommodationsByComfort,
  compareProposalsByComfort,
  getNbParticipants,
  hasErroredProposals,
  hasFetchProposalsFailed,
  getResolvedProposals,
  getSelectedProposal,
  getSelectedProposalById,
  getStockWarning,
  getTotalValue,
  getFormatedPrice,
  getFormatedPriceByLocale,
  getPriceDiff,
  hasTransportOrTransferSelected,
  getDefaultProposal,
  isPremiumAccommodation,
  isExclusiveCollectionRoom,
  isExclusiveCollectionSpace,
  getQuickSearchErrorMessage,
  isExpiredDepartureDate,
};
